import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api, LOCKED_EVENT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function useCountdown(seconds, onDone) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => setLeft(seconds), [seconds]);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => (left === 1 ? onDone() : setLeft(left - 1)), 1000);
    return () => clearTimeout(t);
  }, [left, onDone]);
  return left;
}

const AccessContext = createContext({ user: null, signOut: () => {} });
export const useAccess = () => useContext(AccessContext);

function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function LockScreen({ retryAfter, onUnlocked, onRetry, googleError }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const left = useCountdown(retryAfter, onRetry);
  const blocked = left > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!value || busy || blocked) return;
    setBusy(true);
    try {
      await api.post("/access/unlock", { code: value });
      onUnlocked();
    } catch (err) {
      setValue("");
      setShake(true);
      setTimeout(() => setShake(false), 400);
      if (err.response?.status === 429) onRetry(err.response.data?.retry_after || 300);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6" data-testid="lock-screen">
      <form onSubmit={submit} className={cn("w-full max-w-xs flex gap-2", shake && "animate-shake")}
        data-testid="lock-form">
        <Input ref={inputRef} autoFocus type="password" inputMode="numeric" autoComplete="off"
          aria-label="Invoer" value={value} disabled={blocked} maxLength={64}
          onChange={(e) => setValue(e.target.value)}
          className="h-14 text-center text-xl tracking-[0.3em]" data-testid="lock-input" />
        <Button type="submit" className="h-14 w-14 shrink-0" disabled={!value || busy || blocked}
          aria-label="Bevestigen" data-testid="lock-submit">
          <ArrowRight className="w-5 h-5" />
        </Button>
      </form>
      <div className="w-full max-w-xs mt-6 flex flex-col items-center gap-3">
        <div className="flex items-center gap-3 w-full text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> of <span className="h-px flex-1 bg-border" />
        </div>
        <Button type="button" variant="outline" className="h-14 w-full text-base gap-3" onClick={startGoogleLogin}
          data-testid="google-login-button">
          <GoogleIcon /> Inloggen met Google
        </Button>
        {googleError && <p className="text-sm text-destructive text-center" data-testid="google-login-error">{googleError}</p>}
      </div>
      {blocked && (
        <p className="fixed bottom-10 inset-x-0 text-center text-sm text-muted-foreground" data-testid="lock-blocked">
          Probeer het over {Math.ceil(left / 60)} min opnieuw.
        </p>
      )}
    </div>
  );
}

export function AccessGate({ children }) {
  const [state, setState] = useState({ checked: false, unlocked: false, retryAfter: 0, user: null });
  const [googleError, setGoogleError] = useState("");
  const processed = useRef(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/access/status");
      setState({ checked: true, unlocked: data.unlocked, retryAfter: data.retry_after || 0, user: data.user });
    } catch {
      setState({ checked: true, unlocked: false, retryAfter: 0, user: null });
    }
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    try {
      await api.post("/access/google", { session_id: sessionId });
    } catch (err) {
      setGoogleError(err.response?.data?.detail || "Inloggen met Google is mislukt.");
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    check();
  }, [check]);

  useEffect(() => {
    const match = window.location.hash.match(/session_id=([^&]+)/);
    if (match && !processed.current) {
      processed.current = true;
      exchangeGoogleSession(decodeURIComponent(match[1]));
    } else if (!match) {
      check();
    }
    const lock = () => setState((s) => ({ ...s, unlocked: false }));
    window.addEventListener(LOCKED_EVENT, lock);
    return () => window.removeEventListener(LOCKED_EVENT, lock);
  }, [check, exchangeGoogleSession]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/access/logout");
    } catch {
      // Ook bij een netwerkfout lokaal vergrendelen.
    }
    setState((s) => ({ ...s, unlocked: false, user: null }));
  }, []);

  const onRetry = useCallback((sec) => setState((s) => ({ ...s, retryAfter: sec || 0 })), []);

  if (!state.checked) return <div className="min-h-screen bg-background" data-testid="access-checking" />;
  if (!state.unlocked) {
    return (
      <LockScreen retryAfter={state.retryAfter} onRetry={onRetry} googleError={googleError}
        onUnlocked={() => { setGoogleError(""); check(); }} />
    );
  }
  return <AccessContext.Provider value={{ user: state.user, signOut }}>{children}</AccessContext.Provider>;
}
